import React, { useRef, useEffect, useCallback } from 'react';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
const STROKE_WIDTH = 20;
const FONT_SIZE = 15;
const FADE_INTERVAL = 1000 / 30; // 30fps cap for fade loop

function getSiteImages() {
  return Array.from(document.querySelectorAll('img.homepage-image')).filter(
    img => img.complete && img.naturalWidth > 0
  );
}

// Sample at 1×1 to avoid creating a massive canvas
function sampleSiteColor() {
  const imgs = getSiteImages();
  if (!imgs.length) return null;
  const img = imgs[Math.floor(Math.random() * imgs.length)];
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    const ctx = c.getContext('2d');
    const sx = Math.floor(Math.random() * img.naturalWidth);
    const sy = Math.floor(Math.random() * img.naturalHeight);
    ctx.drawImage(img, sx, sy, 1, 1, 0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `rgb(${r},${g},${b})`;
  } catch (e) {
    return null;
  }
}

function DrawingOverlay({ active = true }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const activeRef = useRef(active);
  const lastPos = useRef(null);
  const lastMid = useRef(null);
  const currentColor = useRef('#d8b1ff');
  const fadeId = useRef(null);
  const mode = useRef('stroke');
  const distSinceLastLetter = useRef(0);
  const segCount = useRef(0);
  const modeSegLimit = useRef(25 + Math.floor(Math.random() * 20));
  const currentImage = useRef(null);
  // Cache section bounds, refreshed at most every 200ms
  const sectionCache = useRef(null);
  const sectionRectCache = useRef(null);
  const sectionRectTime = useRef(0);

  useEffect(() => { activeRef.current = active; }, [active]);

  const getDpr = () => window.devicePixelRatio || 1;

  const applyScale = useCallback((canvas) => {
    canvas.getContext('2d').setTransform(getDpr(), 0, 0, getDpr(), 0, 0);
  }, []);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = getDpr();
    const w = document.body.scrollWidth;
    const h = document.body.scrollHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    applyScale(canvas);
    ctxRef.current = canvas.getContext('2d');
  }, [applyScale]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  useEffect(() => {
    let lastFadeTime = 0;
    const fadeTick = (timestamp) => {
      fadeId.current = requestAnimationFrame(fadeTick);
      if (!activeRef.current) return;
      if (timestamp - lastFadeTime < FADE_INTERVAL) return;
      if (Math.random() > 0.3) {
        lastFadeTime = timestamp;
        const ctx = ctxRef.current;
        if (!ctx) return;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = `rgba(0,0,0,${0.02 + Math.random() * 0.03})`;
        const sx = window.scrollX || 0;
        const sy = window.scrollY || 0;
        ctx.fillRect(sx, sy, window.innerWidth, window.innerHeight);
        ctx.globalCompositeOperation = 'source-over';
      }
    };
    fadeId.current = requestAnimationFrame(fadeTick);
    return () => cancelAnimationFrame(fadeId.current);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        const canvas = canvasRef.current;
        if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width / getDpr(), canvas.height / getDpr());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const getPos = (e) => ({
      x: e.clientX + (window.scrollX || document.documentElement.scrollLeft),
      y: e.clientY + (window.scrollY || document.documentElement.scrollTop),
    });

    const getSection = () => {
      if (!sectionCache.current) sectionCache.current = document.querySelector('.selected-work-section');
      return sectionCache.current;
    };

    // Throttle getBoundingClientRect to at most once per 200ms
    const getSectionRect = () => {
      const now = performance.now();
      if (now - sectionRectTime.current > 200) {
        const section = getSection();
        sectionRectCache.current = section ? section.getBoundingClientRect() : null;
        sectionRectTime.current = now;
      }
      return sectionRectCache.current;
    };

    const draw = (e) => {
      if (!activeRef.current) return;
      const ctx = ctxRef.current;
      if (!ctx) return;

      const rect = getSectionRect();
      if (rect) {
        if (e.clientX < rect.left || e.clientX > rect.right ||
            e.clientY < rect.top || e.clientY > rect.bottom) {
          lastPos.current = null;
          lastMid.current = null;
          return;
        }
      }

      const pos = getPos(e);

      if (!lastPos.current) {
        lastPos.current = pos;
        return;
      }

      const x1 = lastPos.current.x, y1 = lastPos.current.y;
      const x2 = pos.x, y2 = pos.y;
      const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;

      if (!lastMid.current) {
        lastMid.current = { x: midX, y: midY };
        lastPos.current = pos;
        return;
      }

      const dx = x2 - x1, dy = y2 - y1;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

      if (Math.random() < 0.05) {
        const sampled = sampleSiteColor();
        if (sampled) currentColor.current = sampled;
      }

      segCount.current++;
      if (segCount.current >= modeSegLimit.current) {
        segCount.current = 0;
        modeSegLimit.current = 20 + Math.floor(Math.random() * 25);
        const modes = ['stroke', 'letters', 'image'];
        mode.current = modes[(modes.indexOf(mode.current) + 1) % modes.length];
        distSinceLastLetter.current = 0;
        if (mode.current === 'image') {
          const imgs = getSiteImages();
          currentImage.current = imgs.length ? imgs[Math.floor(Math.random() * imgs.length)] : null;
        }
      }

      const fromX = lastMid.current.x, fromY = lastMid.current.y;
      const canvas = canvasRef.current;

      if (mode.current === 'letters') {
        distSinceLastLetter.current += dist;
        if (distSinceLastLetter.current >= FONT_SIZE * 2) {
          distSinceLastLetter.current = 0;
          const letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
          ctx.font = `400 ${FONT_SIZE}px 'Sans', Helvetica`;
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = 0.88;
          ctx.fillText(letter, x2, y2);
          ctx.globalAlpha = 1;
        }
      } else if (mode.current === 'image') {
        const img = currentImage.current;
        if (img && img.complete && img.naturalWidth > 0) {
          const dpr = getDpr();
          const canvasW = canvas.width / dpr;
          const canvasH = canvas.height / dpr;
          const pattern = ctx.createPattern(img, 'no-repeat');
          if (pattern) {
            pattern.setTransform(new DOMMatrix([canvasW / img.naturalWidth, 0, 0, canvasH / img.naturalHeight, 0, 0]));
            ctx.save();
            ctx.filter = 'blur(3px)';
            ctx.beginPath();
            ctx.moveTo(fromX, fromY);
            ctx.quadraticCurveTo(x1, y1, midX, midY);
            ctx.strokeStyle = pattern;
            ctx.lineWidth = STROKE_WIDTH;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.globalAlpha = 0.9;
            ctx.stroke();
            ctx.restore();
          }
        }
      } else {
        const sdx = midX - fromX, sdy = midY - fromY;
        const slen = Math.sqrt(sdx * sdx + sdy * sdy) || 1;
        const lw = STROKE_WIDTH;
        const r = lw / 2;
        const px = -sdy / slen, py = sdx / slen;
        const litSide = ((-0.6) * px + (-0.8) * py) >= 0 ? 1 : -1;
        const cmx = (fromX + midX) / 2, cmy = (fromY + midY) / 2;
        const gx1 = cmx + px * r * litSide, gy1 = cmy + py * r * litSide;
        const gx2 = cmx - px * r * litSide, gy2 = cmy - py * r * litSide;

        const grad = ctx.createLinearGradient(gx2, gy2, gx1, gy1);
        grad.addColorStop(0,    'rgb(0,0,0)');
        grad.addColorStop(0.25, 'rgb(100,100,100)');
        grad.addColorStop(0.55, 'rgb(160,160,160)');
        grad.addColorStop(0.75, 'rgb(210,210,210)');
        grad.addColorStop(0.88, 'rgb(245,245,245)');
        grad.addColorStop(0.95, 'rgb(200,200,200)');
        grad.addColorStop(1,    'rgb(30,30,30)');

        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.quadraticCurveTo(x1, y1, midX, midY);
        ctx.strokeStyle = grad;
        ctx.lineWidth = lw;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.globalAlpha = 1;
        ctx.stroke();
      }

      lastMid.current = { x: midX, y: midY };
      lastPos.current = pos;
    };

    const resetPos = () => { lastPos.current = null; lastMid.current = null; };

    window.addEventListener('mousemove', draw, { passive: true });
    window.addEventListener('mouseleave', resetPos);
    return () => {
      window.removeEventListener('mousemove', draw);
      window.removeEventListener('mouseleave', resetPos);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', top: 0, left: 0, zIndex: 100, pointerEvents: 'none', display: active ? 'block' : 'none' }}
    />
  );
}

export default DrawingOverlay;
